// AppChatPropsFold.ts
// The open chat's property row (Relationship, Status, Phone, ...) folds away (owner review 2026-11-01: the row took a
// third of the chat pane above the conversation). ⭐ FOLDED BY DEFAULT; one header button ("Contact facts", chevron)
// opens it, and the owner's choice is remembered in `localStorage` "wac.chatProps" ("open" / "closed"), like the AI
// panel's "wac.aiPanel". The row is drawn with the `hidden` attribute while folded (`AppChatHead.Props(.., Open)`).
// ⛔ Nothing here can send: the button only toggles an attribute and remembers a word.

import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppBrowserSettings } from "./AppBrowserSettings";
import { AppIcons } from "./AppIcons";

export class AppChatPropsFold {
    public static readonly Key: string = "wac.chatProps";
    public static readonly OpenWord: string = "open";
    public static readonly ClosedWord: string = "closed";
    public static readonly ToggleAttr: string = "data-wac-props-toggle";
    public static readonly ButtonId: string = "wac-chatprops-toggle";
    public static readonly ShowLabel: string = "Show contact facts";
    public static readonly HideLabel: string = "Hide contact facts";
    private static Installed: boolean = false;

    /**
     * True only when the owner opened the row before. Nothing remembered (or storage refused) = folded.
     * ERROR-SUPPRESSED-JUSTIFIED: a storage refusal is "nothing remembered" - the folded default applies.
     * REUSE-JUSTIFIED: one call to the shared AppBrowserSettings reader; the only like code (AppAiPanelStore.Read)
     * is the same one call under another key - a helper would add a hop, not remove logic.
     */
    public static IsOpen = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return nz(AppBrowserSettings.Local().ReadText(AppChatPropsFold.Key).Value) === AppChatPropsFold.OpenWord;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.IsOpen");
            return false;
        }
    };

    /** Remembers the choice; a refused write is simply not remembered next time. */
    public static Remember = (Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Word: string = AppChatPropsFold.ClosedWord;
            if (Open === true) { Word = AppChatPropsFold.OpenWord; }
            AppBrowserSettings.Local().WriteText(AppChatPropsFold.Key, Word);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.Remember");
        }
    };

    /** The header button (icon + label as tooltip and accessible name); "" when the chat has no property row. */
    public static Button = (HasRow: boolean, Open: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (HasRow !== true) { return ""; }
            const Label: string = AppChatPropsFold.LabelOf(Open);
            return "<button type=\"button\" class=\"btn ghost icon chpropsbtn\" id=\"" + AppChatPropsFold.ButtonId + "\" "
                + AppChatPropsFold.ToggleAttr + "=\"1\" aria-controls=\"wac-chatprops\" aria-expanded=\"" + String(Open === true)
                + "\" aria-label=\"" + Label + "\" title=\"" + Label + "\">"
                + AppChatPropsFold.IconOf(Open) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.Button");
            return "";
        }
    };

    /** The button's words for the state it would change. DOCUMENTED EXCEPTION: picks a constant. */
    public static LabelOf = (Open: boolean): string => {
        if (Open === true) { return AppChatPropsFold.HideLabel; }
        return AppChatPropsFold.ShowLabel;
    };

    /** The chevron for the state. */
    public static IconOf = (Open: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Open === true) { return AppIcons.Svg(AppIcons.ChevronUp, 20); }
            return AppIcons.Svg(AppIcons.ChevronDown, 20);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.IconOf");
            return "";
        }
    };

    /** One delegated click listener on the document (idempotent). */
    public static Install = (Doc: Document): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppChatPropsFold.Installed === true) { return; }
            AppChatPropsFold.Installed = true;
            Doc.addEventListener("click", AppChatPropsFold.OnClick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.Install");
        }
    };

    private static OnClick = (Evt: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Evt.target as Element | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            const Btn: Element | null = Target.closest("[" + AppChatPropsFold.ToggleAttr + "]");
            if (Btn === null) { return; }
            const Doc: Document = Btn.ownerDocument;
            const Row: Element | null = Doc.getElementById("wac-chatprops");
            if (Row === null) { return; }
            AppChatPropsFold.Apply(Btn, Row, Row.hasAttribute("hidden"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.OnClick");
        }
    };

    /** Opens or folds the row, swaps the button's words and icon, remembers the choice. */
    public static Apply = (Btn: Element, Row: Element, Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Open === true) { Row.removeAttribute("hidden"); } else { Row.setAttribute("hidden", ""); }
            const Label: string = AppChatPropsFold.LabelOf(Open);
            Btn.setAttribute("aria-expanded", String(Open === true));
            Btn.setAttribute("aria-label", Label);
            Btn.setAttribute("title", Label);
            Btn.innerHTML = AppChatPropsFold.IconOf(Open);
            AppChatPropsFold.Remember(Open);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatPropsFold.Apply");
        }
    };
}
